import type { PresentationPreview } from 'paintscout'

import { PRESENTATION_CANVAS_ID } from './presentationEditorDom'

/**
 * Marks a rendered section wrapper in `Presentation.tsx` with its own `key`.
 *
 * The wrapper already carries `id={section.key}`, which is what `scrollToPresentationKey` looks
 * up, but an `id` cannot be queried as a *set* — there is no way to ask "every section on the
 * page currently rendered" without knowing the keys first. This attribute gives one
 * `querySelectorAll`, so the anchor picker below needs no presentation tree at all.
 */
export const SECTION_KEY_ATTRIBUTE = 'data-section-key'

/**
 * How far below the viewport top a section has to reach before it counts as "in view".
 *
 * Clears the fixed 60px `PresentationEditTopBar`, roughly — the same allowance
 * `presentationCanvasScroll.ts`'s `SCROLL_TOP_INSET` uses for the reverse operation.
 */
export const VIEWPORT_ANCHOR_INSET = 48

export interface SectionRect {
  key: string
  /** Viewport-relative top, i.e. `getBoundingClientRect().top`. */
  top: number
  /** Viewport-relative bottom, i.e. `getBoundingClientRect().bottom`. */
  bottom: number
}

/**
 * The key of the section the editor is looking at: the first one still showing below `inset`.
 *
 * Split out from the DOM read so the geometry is testable. A section the user has scrolled half
 * past is still the one they are working on, so the test is on `bottom`, not `top` — a strict
 * `top >= inset` test would skip to the *next* section as soon as the current one's heading left
 * the screen.
 *
 * Falls back to the last section when every section sits above the inset (the canvas is scrolled
 * to the very bottom), and to `''` when there are no sections at all.
 */
export function pickTopVisibleSectionKey(args: { sections: SectionRect[]; inset?: number }): string {
  const { sections, inset = VIEWPORT_ANCHOR_INSET } = args

  if (!sections?.length) {
    return ''
  }

  const visible = sections.find((section) => section.bottom > inset)

  return (visible ?? sections.at(-1)).key
}

/**
 * Reads the editor canvas and returns the key of the section currently in view.
 *
 * Only the active page's sections are ever in the DOM (#21990), so the key this returns also
 * identifies the page — callers resolve it with `resolvePageForKey` rather than tracking a page
 * key separately.
 *
 * Must be called while the canvas is still mounted: preview mode replaces it with an iframe, so a
 * read taken after the swap finds nothing and returns `''`.
 */
export function getVisibleSectionKey(args?: { doc?: Document; inset?: number }): string {
  const { doc = document, inset } = args ?? {}
  const canvas = doc.getElementById(PRESENTATION_CANVAS_ID)

  if (!canvas) {
    return ''
  }

  const sections: SectionRect[] = Array.from(canvas.querySelectorAll(`[${SECTION_KEY_ATTRIBUTE}]`)).map((element) => {
    const { top, bottom } = element.getBoundingClientRect()

    return { key: element.getAttribute(SECTION_KEY_ATTRIBUTE) ?? '', top, bottom }
  })

  return pickTopVisibleSectionKey({ sections, inset })
}

/**
 * The anchor the presentation context should carry after a preview-mode change.
 *
 * Three cases:
 * - leaving preview — no anchor;
 * - edit → preview — read the canvas, which is the only moment it is still mounted;
 * - preview → another device — keep the anchor, because there is no canvas left to read.
 *
 * `readVisibleSectionKey` is a callback rather than a value so the DOM read happens on the capture
 * branch only. Called with a value, every device switch inside preview would measure an unmounted
 * canvas and overwrite a good anchor with `''`.
 */
export function nextPreviewAnchorKey(args: {
  nextPreview: PresentationPreview
  currentPreview: PresentationPreview
  currentAnchorKey: string
  readVisibleSectionKey: () => string
}): string {
  const { nextPreview, currentPreview, currentAnchorKey, readVisibleSectionKey } = args

  if (!nextPreview || nextPreview === 'none') {
    return ''
  }

  const leavingEditor = !currentPreview || currentPreview === 'none'

  return leavingEditor ? readVisibleSectionKey() : (currentAnchorKey ?? '')
}
